import { useState } from "react";
import styled from "styled-components";
import { Button, Input, Popconfirm, Spin, Empty } from "antd";
import { EditOutlined, DeleteOutlined } from "@ant-design/icons";
import { Consult } from "../../api/consultAPI";

interface Props {
  consults: Consult[];
  currentUser?: string;
  loading?: boolean;
  onAdd: (content: string) => Promise<void> | void;
  onEdit: (consultId: number, content: string) => Promise<void> | void;
  onDelete: (consultId: number) => Promise<void> | void;
}

const formatDateTime = (iso: string) => {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(
    d.getMinutes()
  )}`;
};

const ConsultThread = ({ consults, currentUser, loading, onAdd, onEdit, onDelete }: Props) => {
  const [newText, setNewText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingText, setEditingText] = useState("");

  const handleAdd = async () => {
    const content = newText.trim();
    if (!content || submitting) return;
    setSubmitting(true);
    try {
      await onAdd(content);
      setNewText("");
    } finally {
      setSubmitting(false);
    }
  };

  const startEdit = (c: Consult) => {
    setEditingId(c.consultId);
    setEditingText(c.content);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingText("");
  };

  const submitEdit = async (consultId: number) => {
    const content = editingText.trim();
    if (!content) return;
    await onEdit(consultId, content);
    cancelEdit();
  };

  return (
    <Wrapper>
      <SectionTitle>자문</SectionTitle>

      {/* 목록 (위에서부터 누적, 최신순) */}
      {loading ? (
        <Center>
          <Spin />
        </Center>
      ) : consults.length === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="등록된 자문이 없습니다." style={{ marginTop: 24 }} />
      ) : (
        <List>
          {consults.map((c) => {
            const isOwn = !!currentUser && c.author === currentUser;
            const isEditing = editingId === c.consultId;
            return (
              <Item key={c.consultId}>
                <ItemHead>
                  <Author>{c.author || "익명"}</Author>
                  <HeadRight>
                    <DateText>
                      {formatDateTime(c.createdAt)}
                      {c.updatedAt ? " (수정됨)" : ""}
                    </DateText>
                    {isOwn && !isEditing && (
                      <Actions>
                        <IconButton type="text" size="small" icon={<EditOutlined />} onClick={() => startEdit(c)} />
                        <Popconfirm
                          title="이 자문을 삭제하시겠습니까?"
                          okText="삭제"
                          cancelText="취소"
                          onConfirm={() => onDelete(c.consultId)}
                        >
                          <IconButton type="text" size="small" danger icon={<DeleteOutlined />} />
                        </Popconfirm>
                      </Actions>
                    )}
                  </HeadRight>
                </ItemHead>

                {isEditing ? (
                  <EditArea>
                    <Input.TextArea
                      value={editingText}
                      onChange={(e) => setEditingText(e.target.value)}
                      autoSize={{ minRows: 3, maxRows: 10 }}
                    />
                    <EditActions>
                      <Button size="small" onClick={cancelEdit}>
                        취소
                      </Button>
                      <Button size="small" type="primary" onClick={() => submitEdit(c.consultId)} disabled={!editingText.trim()}>
                        저장
                      </Button>
                    </EditActions>
                  </EditArea>
                ) : (
                  <Content>{c.content}</Content>
                )}
              </Item>
            );
          })}
        </List>
      )}

      {/* 작성 영역 (free text) — 누적된 자문 블록 아래에 위치 */}
      <Composer>
        <Input.TextArea
          value={newText}
          onChange={(e) => setNewText(e.target.value)}
          placeholder="자문 내용을 입력하세요."
          autoSize={{ minRows: 3, maxRows: 8 }}
        />
        <ComposerActions>
          <Button type="primary" onClick={handleAdd} loading={submitting} disabled={!newText.trim()}>
            자문 등록
          </Button>
        </ComposerActions>
      </Composer>
    </Wrapper>
  );
};

export default ConsultThread;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const SectionTitle = styled.div`
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: #8c8c8c;
  margin: 4px 0 10px;
`;

const Composer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 18px;
`;

const ComposerActions = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const Center = styled.div`
  display: flex;
  justify-content: center;
  padding: 24px 0;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Item = styled.div`
  border: 1px solid #ececec;
  border-radius: 10px;
  background: #fafafa;
  padding: 12px 14px;
`;

const ItemHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 8px;
`;

const Author = styled.span`
  font-size: 0.88rem;
  font-weight: 600;
  color: #1e2e3a;
`;

const HeadRight = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const DateText = styled.span`
  font-size: 0.75rem;
  color: #9b9a97;
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
`;

const IconButton = styled(Button)`
  color: #8c8c8c;
`;

const Content = styled.div`
  font-size: 0.92rem;
  color: #262626;
  line-height: 1.6;
  white-space: pre-wrap;
  word-break: break-word;
`;

const EditArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const EditActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 6px;
`;
